import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import NavButton from '@atoms/NavButton/NavButton';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import { ITabbedFeatureContent } from '@models/ITabbedFeatureContent';
import { IVideo } from '@models/IVideo';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import TabbedFeatureSection from './TabbedFeatureSection';
import TabbedFeatureSlide from './TabbedFeatureSlide';

interface Props {
  readonly title?: string;
  readonly primaryCta?: ILink;
  readonly tabs?: ITabbedFeatureContent[];
  readonly wideContent?: boolean;
  readonly anchorId?: string;
  readonly meta?: IMeta;
  readonly showMoreLabel: string;
  readonly showLessLabel: string;
}

const TabbedFeature = ({
  title,
  primaryCta,
  tabs = [],
  wideContent = false,
  anchorId = '',
  meta,
  showMoreLabel,
  showLessLabel,
}: Props) => {
  const isMobileViewport = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);
  const activeTab = tabs.find((_, index) => index === activeIndex);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;
  const navButtonId = 'tabbed-feature-carousel';

  return (
    <section
      className="component-padding bg-black-100 text-white"
      id={`a-${anchorId}`}
      data-testid="CT-49-Tabbed-Feature"
    >
      <SoftLoadWrapper>
        <div className="lg:container">
          <div className="flex w-full flex-col px-5 lg:flex-row lg:items-center lg:px-0">
            <h2
              className={isMobileViewport ? 'h5' : 'h3'}
              data-epi-property-name={meta?.editHints?.title}
              data-testid="CT-49-Tabbed-Feature-Title"
            >
              {title}
            </h2>

            {primaryCta?.name && primaryCta?.url && (
              <div className="mt-6 flex items-center justify-between lg:mt-0 lg:ml-auto">
                <Button
                  link={primaryCta}
                  editHint={
                    meta?.editHints?.primaryCta
                      ? `${meta.editHints.primaryCta}`
                      : undefined
                  }
                  dataTestId="CT-49-Tabbed-Feature-CTA"
                />

                <div
                  className={classNames(
                    'items-center justify-end',
                    isZoomedDesktop ? 'flex' : 'hidden'
                  )}
                >
                  <div className="flex gap-x-[25px]">
                    <NavButton
                      direction={HorizontalDirectionEnum.Left}
                      id={navButtonId}
                      disabled={isSwiperBeginning}
                    />

                    <NavButton
                      direction={HorizontalDirectionEnum.Right}
                      id={navButtonId}
                      disabled={isSwiperEnd}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {isMobileViewport ? (
            <div
              className="mt-10"
              data-testid="CT-49-Tabbed-Feature-Carousel-Container"
            >
              <Carousel
                slidesOffsetAfter={20}
                slidesOffsetBefore={20}
                spaceBetween={20}
                enabled
                navId={navButtonId}
                breakpoints={{
                  0: {
                    slidesPerView: 1.3,
                  },
                  480: {
                    slidesPerView: 1.8,
                  },
                  768: {
                    slidesPerView: 2.3,
                  },
                }}
                onSetIsSwiperBeginning={setIsSwiperBeginning}
                onSetIsSwiperEnd={setIsSwiperEnd}
              >
                {tabs.map((tab, index) => (
                  <SwiperSlide key={tab.title}>
                    <TabbedFeatureSlide
                      tab={tab}
                      wideContent={wideContent}
                      dataTestId={`CT-49-Tabbed-Feature-Tab-${index}`}
                    />
                  </SwiperSlide>
                ))}
              </Carousel>
            </div>
          ) : (
            <div className="mt-13 flex flex-row items-center overflow-hidden">
              <div
                className={classNames(
                  'relative flex flex-initial flex-col gap-10 border-grey-500 border-l pr-10 xl:gap-20',
                  wideContent ? 'w-7/12 xl:w-6/12' : 'w-6/12 xl:w-4/12'
                )}
                data-epi-property-name={meta?.editHints?.tabs}
              >
                {tabs.map((tab, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <TabbedFeatureSection
                      key={tab.title}
                      tab={tab}
                      isActive={isActive}
                      index={index}
                      dataTestId={`CT-49-Tabbed-Feature-Tab-${index}`}
                      showMoreLabel={showMoreLabel}
                      showLessLabel={showLessLabel}
                      onSetActiveIndex={setActiveIndex}
                    />
                  );
                })}
              </div>

              <div
                className={classNames(
                  'ml-auto',
                  wideContent ? 'w-5/12 xl:w-6/12' : 'w-6/12 xl:w-7/12'
                )}
              >
                <AnimatePresence key={activeTab?.title} mode="wait">
                  <motion.div
                    key="media"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      hidden: {
                        y: 30,
                        opacity: 0,
                        transition: {
                          ease: [0.42, 0.46, 0.45, 0.99],
                          duration: 0.5,
                        },
                      },
                      visible: {
                        y: 0,
                        opacity: 1,
                        transition: {
                          ease: [0.42, 0.46, 0.45, 0.99],
                          duration: 0.5,
                        },
                      },
                    }}
                  >
                    <div
                      className={wideContent ? 'aspect-square' : 'aspect-3/2'}
                    >
                      {activeTab?.media &&
                      'url' in activeTab.media &&
                      activeTab.media.url ? (
                        <InViewVideo
                          video={activeTab.media as IVideo}
                          videoClasses="[&>div>video]:object-cover h-full"
                          wrapperClasses="w-full h-full"
                          autoPlay
                          hasInVideoPlayButton
                          controls={false}
                          dataTestId="CT-49-Tabbed-Feature-Active-Tab-Video"
                        />
                      ) : (
                        <Image
                          image={activeTab?.media as IImage}
                          classes="h-full"
                          imageClasses="h-full w-full object-cover"
                          dataTestId="CT-49-Tabbed-Feature-Active-Tab-Image"
                        />
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default TabbedFeature;
